import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NgxPaginationModule } from 'ngx-pagination';
import { FullSpinnerService } from '../../../services/full-spinner.service';
import { AppConstants } from '../../../utils/AppConstants';
import { TooltipDirective } from '../../../utils/tooltip.directive';
import { HasPermissionDirective } from '../../../utils/has-permission.directive';
import { ConfirmAlertComponent } from '../../shared/confirm-alert/confirm-alert.component';
import { AlertService } from '../../shared/alert/alert.service';
import { Account, ACCOUNT_ROLES } from '../../../models/Account';
import { AccountService } from '../../../services/account.service';
import { AccountSearchCriteria } from '../../../models/searchCriteria/AccountSearchCriteria';
import { Organization } from '../../../models/Organization';
import { OrganizationService } from '../../../services/organization.service';

@Component({
  selector: 'app-account-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgxPaginationModule,
    ConfirmAlertComponent,
    HasPermissionDirective,
    TooltipDirective,
  ],
  templateUrl: './account-list.component.html',
  styleUrl: './account-list.component.scss',
})
export class AccountListComponent implements OnInit {

  organizations: Organization[] = [];

  emptySearchCriteria: AccountSearchCriteria = { organizationId: undefined, username: undefined, pageSize: AppConstants.pageSize, pageNumber: 1 };
  searchCriteria: AccountSearchCriteria = { ...this.emptySearchCriteria };

  accounts: Account[] = [];
  totalItems: number = 0;

  showConfirmationModal: boolean = false;
  confirmationMessage: string = '';
  selectedAccount?: Account;

  constructor(
    private accountService: AccountService,
    private organizationService: OrganizationService,
    private alertService: AlertService,
    private fullSpinnerService: FullSpinnerService,
    private router: Router) {
    fullSpinnerService.spinner(undefined);
  }

  ngOnInit(): void {
    this.loadOrganizations();
    this.find();
  }

  private loadOrganizations(): void {
    this.organizationService.find({ pageSize: 1000, pageNumber: 1 }).subscribe({
      next: (res) => { this.organizations = res.items; },
      error: () => {},
    });
  }

  find(): void {
    this.fullSpinnerService.spinner('');
    this.accountService.find(this.searchCriteria).subscribe({
      next: (res) => {
        this.accounts = res.items;
        this.totalItems = res.totalItems;
      },
      complete: () => { this.fullSpinnerService.spinner(undefined); },
      error: () => { this.fullSpinnerService.spinner(undefined); },
    });
  }

  search(): void {
    this.searchCriteria.pageNumber = 1;
    this.find();
  }

  clearFilter(): void {
    this.searchCriteria = { ...this.emptySearchCriteria };
    this.find();
  }

  pageChanged(event: number): void {
    this.searchCriteria.pageNumber = event;
    this.find();
  }

  sync(): void {
    this.fullSpinnerService.spinner('');
    this.accountService.sync().subscribe({
      next: () => {
        this.alertService.addErrors(AppConstants.titleSuccessMessage, [AppConstants.successMessage], true);
        this.fullSpinnerService.spinner(undefined);
        this.find();
      },
      error: () => {
        this.alertService.addErrors(AppConstants.titleErrorMessage, [AppConstants.errorMessage], true);
        this.fullSpinnerService.spinner(undefined);
      },
    });
  }

  roleLabel(role?: string): string {
    return ACCOUNT_ROLES.find((r) => r.id === role)?.description ?? (role ?? '');
  }

  add(): void {
    this.router.navigate(['accounts', 'new'], { queryParams: { organizationId: this.searchCriteria.organizationId } });
  }

  edit(account: Account): void {
    this.router.navigate(['accounts', 'edit', account.accountId]);
  }

  confirmDisable(account: Account): void {
    this.showConfirmationModal = true;
    this.confirmationMessage = 'Disabilitazione account - Sicuro di voler disabilitare ' + account.username + ' ?';
    this.selectedAccount = account;
  }

  cancelDisable(): void {
    this.selectedAccount = undefined;
    this.showConfirmationModal = false;
  }

  disableExec(): void {
    this.fullSpinnerService.spinner('');
    this.accountService.disable(this.selectedAccount!.accountId!).subscribe({
      next: () => {
        this.alertService.addErrors(AppConstants.titleSuccessMessage, [AppConstants.successMessage], true);
        this.cancelDisable();
        this.fullSpinnerService.spinner(undefined);
        this.find();
      },
      error: () => {
        this.cancelDisable();
        this.alertService.addErrors(AppConstants.titleErrorMessage, [AppConstants.errorMessage], true);
        this.fullSpinnerService.spinner(undefined);
      },
    });
  }
}
